import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstateWithProjectDetails } from '@/features/estates/api/estates';
import {
  AddressAutocomplete,
  type OnSelectSuggestion,
} from '@/features/estates/components/address-autocomplete';
import { type EstateSchemaType } from '@/features/estates/schemas/estate';
import { searchAllQueryKey } from '@/features/search/api/search';
import {
  getTenantProjectsQueryOptions,
  useCreateTenantProject,
  useTenantProjects,
} from '@/features/tenants/api/tenant-projects';

import type { EstateProjectSearchItem } from '../types';
import { emptyAddressData, getCreateEstateProjectSchema } from './create-estate-project-schema';

type CreateEstateProjectFormProps = {
  onCancel: () => void;
  onCreated: (item: EstateProjectSearchItem) => void;
  onCreatingChange?: (isCreating: boolean) => void;
};

export const CreateEstateProjectForm: FC<CreateEstateProjectFormProps> = ({
  onCancel,
  onCreated,
  onCreatingChange,
}) => {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: tenants } = useMyTenants();
  const queryClient = useQueryClient();

  const defaultValues = useMemo<EstateSchemaType>(
    () => ({
      estateData: {
        name: '',
        propertyType: '',
        marketType: '',
        tenantId: tenants[0]?.id ?? '',
        projectId: undefined,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: emptyAddressData,
    }),
    [tenants]
  );

  const {
    control,
    formState: { errors, submitCount },
    clearErrors,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<EstateSchemaType>({
    resolver: zodResolver(getCreateEstateProjectSchema()),
    defaultValues,
  });

  const propertyLevel = watch('estateData.propertyLevel');
  const tenantId = watch('estateData.tenantId');
  const projectId = watch('estateData.projectId') || '';
  const { data: projects = [] } = useTenantProjects(tenantId);

  const tenantOptions = useMemo(
    () =>
      tenants.map((tenant) => ({
        title: tenant.name,
        id: tenant.id,
      })),
    [tenants]
  );

  const projectOptions = useMemo<SelectOption[]>(
    () =>
      projects.map((project) => ({
        title: project.name || `Project_${project.id}`,
        id: project.id,
      })),
    [projects]
  );

  const { mutateAsync: createEstateAsync, isPending: isCreatingEstate } =
    useCreateEstateWithProjectDetails({
      mutationConfig: {
        onSuccess: async ([createdEstate]) => {
          await queryClient.invalidateQueries({ queryKey: ['estates'] });
          await queryClient.invalidateQueries({ queryKey: ['projects', 'estates', projectId] });
          await queryClient.invalidateQueries({ queryKey: searchAllQueryKey });
          toast.success(t('create-estate-success'));
          onCreated({
            id: createdEstate.id,
            name: createdEstate.name ?? t('noName'),
            type: 'estate',
          });
          reset(defaultValues);
        },
        onError: (err) => {
          toast.error(err.message);
        },
      },
    });

  const { mutateAsync: createProjectAsync, isPending: isCreatingProject } = useCreateTenantProject({
    mutationConfig: {
      onSuccess: async ([createdProject], variables) => {
        await queryClient.invalidateQueries({ queryKey: ['projects'] });
        await queryClient.invalidateQueries({
          queryKey: getTenantProjectsQueryOptions(variables.tenantId).queryKey,
        });
        await queryClient.invalidateQueries({ queryKey: ['estates'] });
        await queryClient.invalidateQueries({ queryKey: searchAllQueryKey });
        toast.success(t('create-project-success'));
        onCreated({
          id: createdProject.id,
          name: createdProject.name ?? t('noName'),
          type: 'project',
        });
        reset(defaultValues);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const onSelectSuggestion: OnSelectSuggestion = ({
    houseNumber,
    city,
    street,
    address,
    latitude,
    longitude,
    countryId,
    postalCode,
  }) => {
    const fieldStateOptions = {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    };

    if (address) {
      setValue('addressData.address', address, fieldStateOptions);
      setValue('addressData.addressAutoComplete', address, fieldStateOptions);
    }

    setValue('addressData.street', street ?? '', fieldStateOptions);
    setValue('addressData.houseNumber', houseNumber ?? '', fieldStateOptions);
    setValue('addressData.city', city ?? '', fieldStateOptions);
    setValue('addressData.postalCode', postalCode ?? '', fieldStateOptions);

    setValue('addressData.countryId', countryId ?? '', fieldStateOptions);
    if (latitude != null) setValue('addressData.lat', latitude, fieldStateOptions);
    if (longitude != null) setValue('addressData.long', longitude, fieldStateOptions);
    clearErrors(['addressData.address', 'addressData.addressAutoComplete']);
  };

  const isCreating = isCreatingEstate || isCreatingProject;

  useEffect(() => {
    onCreatingChange?.(isCreating);

    return () => {
      onCreatingChange?.(false);
    };
  }, [isCreating, onCreatingChange]);

  useEffect(() => {
    const defaultTenantId = tenants[0]?.id;

    if (!tenantId && defaultTenantId) {
      setValue('estateData.tenantId', defaultTenantId, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }, [setValue, tenantId, tenants]);

  const cancelCreate = () => {
    reset(defaultValues);
    onCancel();
  };

  const onSubmit = handleSubmit(async (formValues) => {
    if (formValues.estateData.propertyLevel === PROPERTY_LEVELS.PROJECT) {
      await createProjectAsync({
        projectData: {
          ...formValues.estateData,
          status: 'draft',
          isActive: 0,
          creatorId: me?.id,
        },
        addressData: formValues.addressData,
        tenantId: formValues.estateData.tenantId,
      });
      return;
    }

    await createEstateAsync({
      ...formValues,
      estateData: {
        ...formValues.estateData,
        creatorId: me?.id,
      },
    });
  });

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <Controller
        name="estateData.name"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="sm"
            label={`${t('name')} *`}
            placeholder={t('name')}
            className="border-slate-200 bg-white text-sm text-slate-600 placeholder:text-slate-400 focus-visible:border-primary-light"
            errors={[errors.estateData?.name?.message || '']}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Controller
          name="estateData.propertyLevel"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              size="sm"
              color="neutral"
              label={`${t('propertyLevel')} *`}
              options={[
                { title: t('estate'), id: PROPERTY_LEVELS.OBJECT },
                { title: t('project'), id: PROPERTY_LEVELS.PROJECT },
              ]}
              className="bg-white text-slate-600 hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-slate-500"
              errors={[errors.estateData?.propertyLevel?.message || '']}
              onChange={(event) => {
                field.onChange(event);
                setValue('estateData.projectId', undefined, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }}
            />
          )}
        />

        {propertyLevel === PROPERTY_LEVELS.OBJECT ? (
          <Controller
            name="estateData.projectId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                size="sm"
                color="neutral"
                label={t('project')}
                placeholder={t('project')}
                options={projectOptions}
                className="bg-white text-slate-600 hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-slate-500"
                errors={[errors.estateData?.projectId?.message || '']}
                disabled={!tenantId}
              />
            )}
          />
        ) : null}
      </div>

      <Controller
        name="estateData.tenantId"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            size="sm"
            color="neutral"
            label={`${t('tenant')} *`}
            placeholder={t('tenant')}
            options={tenantOptions}
            className="bg-white text-slate-600 hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-slate-500"
            errors={[errors.estateData?.tenantId?.message || '']}
            onChange={(event) => {
              field.onChange(event);
              setValue('estateData.projectId', undefined, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
            }}
          />
        )}
      />

      <Controller
        name="addressData.addressAutoComplete"
        control={control}
        render={({ field }) => (
          <AddressAutocomplete
            {...field}
            size="sm"
            label={`${t('address')} *`}
            placeholder={t('address')}
            className="border-slate-200 bg-white text-sm text-slate-600 placeholder:text-slate-400 focus-visible:border-primary-light"
            onChange={(event) => {
              const clearFieldStateOptions = {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
              };

              field.onChange(event);
              setValue('addressData.address', event.target.value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
              });
              setValue('addressData.street', '', clearFieldStateOptions);
              setValue('addressData.houseNumber', '', clearFieldStateOptions);
              setValue('addressData.city', '', clearFieldStateOptions);
              setValue('addressData.postalCode', '', clearFieldStateOptions);
              setValue('addressData.countryId', '', clearFieldStateOptions);
              setValue('addressData.lat', undefined, clearFieldStateOptions);
              setValue('addressData.long', undefined, clearFieldStateOptions);
              clearErrors(['addressData.address', 'addressData.addressAutoComplete']);
            }}
            onSelectSuggestion={onSelectSuggestion}
            errors={[
              submitCount > 0
                ? errors.addressData?.addressAutoComplete?.message ||
                  errors.addressData?.address?.message ||
                  ''
                : '',
            ]}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[8rem_1fr]">
        <Button
          type="button"
          variant="outline"
          color="primary"
          size="md"
          className="text-sm font-semibold"
          disabled={isCreating}
          onClick={cancelCreate}
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          size="md"
          className="text-sm font-semibold"
          loading={isCreating}
          disabled={isCreating}
        >
          {t('createAndContinue')}
        </Button>
      </div>
    </form>
  );
};
